import React from "react";
import { MapContainer, TileLayer, Circle } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin } from "lucide-react";

/**
 * Locked, tile-blurred Leaflet map showing a coarse "hunt zone" circle.
 * Receives a zone = { lat, lng, radius_m } already coarsened server-side
 * (rounded to 2 decimals ≈ 1.1 km precision). No interaction, no labels —
 * it's a teaser, not a navigation tool.
 */
export default function HuntZoneMap({
  zone,
  height = 160,
  label = "Hunt zone",
  sub = "Blurred until live",
  tint = "#76B82A",
}) {
  const hasZone = zone && typeof zone.lat === "number" && typeof zone.lng === "number";

  if (!hasZone) {
    return (
      <div className="loot-card-flat p-0 overflow-hidden" data-testid="hunt-zone-placeholder">
        <div className="map-blur relative" style={{ height }}>
          <div className="absolute inset-0 flex items-center justify-center">
            <MapPin size={44} style={{ color: tint }} strokeWidth={2.5} />
          </div>
        </div>
        <div className="p-4 flex items-center justify-between">
          <div className="overline">{label}</div>
          <div className="font-mono text-xs text-[#F5F2E8]/60">{sub}</div>
        </div>
      </div>
    );
  }

  const center = [zone.lat, zone.lng];
  const radius = Math.max(600, Number(zone.radius_m) || 1000);

  return (
    <div className="loot-card-flat p-0 overflow-hidden" data-testid="hunt-zone-map">
      <div className="relative" style={{ height }}>
        <MapContainer
          center={center}
          zoom={13}
          zoomControl={false}
          dragging={false}
          scrollWheelZoom={false}
          doubleClickZoom={false}
          touchZoom={false}
          keyboard={false}
          boxZoom={false}
          attributionControl={false}
          style={{ height: "100%", width: "100%", background: "#0f1f12" }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            className="loot-map-tiles"
          />
          {/* Outer blur ring */}
          <Circle
            center={center}
            radius={radius * 1.35}
            pathOptions={{
              color: tint,
              fillColor: tint,
              fillOpacity: 0.08,
              opacity: 0.25,
              weight: 1,
              dashArray: "4 6",
            }}
          />
          {/* Core zone ring */}
          <Circle
            center={center}
            radius={radius}
            pathOptions={{
              color: tint,
              fillColor: tint,
              fillOpacity: 0.18,
              opacity: 0.85,
              weight: 2,
            }}
          />
        </MapContainer>
        {/* Corner overlay — signals non-interactive */}
        <div
          aria-hidden="true"
          className="absolute top-2 left-2 font-mono text-[10px] uppercase tracking-widest px-2 py-1 border"
          style={{
            borderColor: "rgba(118,184,42,0.6)",
            background: "rgba(15,15,15,0.75)",
            color: "#76B82A",
          }}
        >
          GPS · ZONE
        </div>
      </div>
      <div className="p-4 flex items-center justify-between">
        <div className="overline">{label}</div>
        <div className="font-mono text-xs text-[#F5F2E8]/60">{sub}</div>
      </div>
    </div>
  );
}
